<script>
import UserResource from '../resources/user'
import ImageResource from '../resources/image'
import CompanyResource from '../resources/company'
import DeveloperProfileResource from '../resources/developerprofile'
import messages from '../mixins/messages'
import LoginForm from '../components/LoginForm.vue'
import CompanyRegisterForm from '../components/CompanyRegisterForm.vue'
import JobPostingForm from '../components/JobPostingForm.vue'
import JobPosting from '../components/JobPosting.vue'
import ImageUpload from '../components/ImageUpload.vue'
import ProfileCell from '../components/ProfileCell.vue'

export default {
    name: 'SignupCompany',
    mixins: [messages],
    components: {
        CompanyRegisterForm,
        ImageUpload,
        JobPostingForm,
        JobPosting,
        LoginForm,
        ProfileCell,
    },
    data() {
        return {
            step: 1,
            loading: false,
            accountFormState: {},
            passwordFormState: {},
            jobFormState: {},
            userResource: null,
            companyResource: null,
            imageResource: null,
            companyOptions: [],
            companyImage: null,
            companyImageSelected: false,
            listingResource: null,
            demoProfile: null,
            hasAccount: false,
            accountModel: {
                email: null,
                firstName: null,
                lastName: null,
                password: null,
                passwordConfirm: null,
            },
        }
    },
    watch: {
        async userResource() {
            let userCompaniesResult = await CompanyResource.filter({ owner: this.userResource.id })

            this.companyOptions = userCompaniesResult.resources.map((companyResource) => {
                return {
                    value: companyResource,
                    text: companyResource.toString()
                }
            })
        }
    },
    methods: {
        async onSubmitStep1(e) {
            try {
                this.loading = true

                if (this.accountFormState.$invalid) {
                    return false
                }

                this.userResource = new UserResource({
                    email: this.accountModel.email,
                    username: this.accountModel.email,
                    first_name: this.accountModel.firstName,
                    last_name: this.accountModel.lastName,
                    password: this.randomStringGenerate(20),
                })

                await this.userResource.save()
                await this.$store.dispatch('login', {
                    username: this.userResource.get('email'),
                    password: this.userResource.get('password'),
                })
                
                this.setStep(2)
            } catch (e) {
                this.addError(this.parseAPIErrorsOrRethrow(e))
            } finally {
                this.loading = false
            }
        },
        async onSubmitStep2(e) {
            try {
                this.loading = true

                if (this.passwordFormState.$invalid) {
                    return false
                }

                this.userResource.setPassword(this.accountModel.password)
                await this.userResource.save()
                this.setStep(3)
            } catch (e) {
                this.addError(this.parseAPIErrorsOrRethrow(e))
            } finally {
                this.loading = false
            }
        },
        async onSubmitStep3(companyResource) {
            try {
                this.loading = true
                await companyResource.save()
                this.companyResource = companyResource
                // Image
                if (this.companyImage) {
                    this.imageResource = ImageResource.fromDataURL(this.companyImage)
                    await this.imageResource.save()
                    this.companyResource.set('image', this.imageResource.id)
                    await this.companyResource.save()
                }
                this.setStep(4)
            } catch (e) {
                this.addError(this.parseAPIErrorsOrRethrow(e))
            } finally {
                this.loading = false
            }
        },
        async onSubmitStep4(listingResource) {
            try {
                this.loading = true
                await listingResource.save()
                this.listingResource = listingResource
                this.$router.push({
                    name: 'recruitJobView',
                    params: {
                        companyId: this.companyResource.id,
                        listingId: this.listingResource.id
                    }
                })
            } catch (e) {
                this.addError(this.parseAPIErrorsOrRethrow(e))
            } finally {
                this.loading = false
            }
        },
        async onSubmitLogin(userResource) {
            this.userResource = userResource
            this.setStep(3)
        },
        setStep(number) {
            this.$router.push({ hash: '#step-' + number })
            this.step = parseInt(number)
        },
        randomStringGenerate(length = 20) {
            return (Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)).substring(0, length)
        },
        getStepFromHash() {
            this.step = parseInt(this.$route.hash.replace('#', '').replace(/^step-(.*)$/, '$1'))
        },
        onCropperSave(dataURL) {
            this.companyImage = dataURL
            this.companyImageSelected = true
        },
    },
    async mounted() {
        // Parse step hash
        if (this.$route.hash && this.$route.hash.match(/^#step-/)) {
            this.getStepFromHash()
        }

        if (this.$store.getters.userResource) {
            this.userResource = this.$store.getters.userResource
            this.hasAccount = true
            this.setStep(3)
        }

        if (this.step === 1) {
            this.$refs.emailInput.$el.focus()
        }

        this.demoProfile = await DeveloperProfileResource.random()
        await this.demoProfile.resolveRelated({ managers: ['image', 'skills'] })
    },
}
</script>

<template>
    <div v-page-title="'Create a Company Profile'">
        <section>
            <div class="section-heading text-center mb-0 mt-5">
                <p>Create a Job Listing</p>
                <h2>Just a Few Steps Away...</h2>
            </div>
        </section>

        <section class="p-md-5 mt-5 gray">
            <b-container>
                <b-row>
                    <b-col md="10" offset-md="1">
                        <b-tabs fill>
                            <b-tab :active="step <= 2" class="p-5" :disabled="step > 2">
                                <template slot="title">
                                    <span v-if="step > 2" class="text-success"> <b-icon icon="check" />Account </span>
                                    <span v-else>Account</span>
                                </template>

                                <b-row v-if="step == 1">
                                    <b-col md="6">
                                        <b-row>
                                            <b-col sm="2">
                                                <p class="h1"><b-icon icon="code-slash" /></p>
                                            </b-col>
                                            <b-col>
                                                <h4>Highest-quality Candidates</h4>
                                                <p>
                                                    Post a job with us and we'll expose you to the highest quality candidates looking for positions using <strong>Node.js, JavaScript</strong>, and many other tech stacks.
                                                </p>
                                            </b-col>
                                        </b-row>

                                        <b-row class="mt-3">
                                            <b-col sm="2">
                                                <p class="h1">
                                                    <b-icon icon="search" />
                                                </p>
                                            </b-col>
                                            <b-col>
                                                <h4>Self Serve</h4>
                                                <p>
                                                    With <strong>thousands of developers</strong> at your fingertips, <strong>and more joining every day</strong>, find candidates who meet your requirements <em>proactively</em>.
                                                </p>
                                            </b-col>
                                        </b-row>
                                    </b-col>

                                    <b-col md="6">
                                        <b-card v-if="hasAccount">
                                            <login-form @success="onSubmitLogin" @register="hasAccount = false" submit-text="Next"></login-form>
                                        </b-card>

                                        <b-card v-else>
                                            <vue-form :state="accountFormState" @submit.prevent="onSubmitStep1">
                                                <validate auto-label>
                                                    <b-form-group label="Email Address">
                                                        <b-form-input type="email" name="email" v-model="accountModel.email" ref="emailInput" :disabled="!!userResource" required />
                                                        <field-messages name="email" show="$submitted">
                                                            <div slot="required" class="text-danger">
                                                                Email is required
                                                            </div>
                                                            <div slot="email" class="text-danger">
                                                                Email is invalid
                                                            </div>
                                                            <div slot="email-exists" class="text-danger">
                                                                Email already exists
                                                            </div>
                                                        </field-messages>
                                                    </b-form-group>
                                                </validate>

                                                <validate auto-label>
                                                    <b-form-group label="First Name">
                                                        <b-form-input name="firstName" v-model="accountModel.firstName" :disabled="!!userResource" required />
                                                        <field-messages name="firstName" show="$submitted">
                                                            <div slot="required" class="text-danger">
                                                                This field is required
                                                            </div>
                                                        </field-messages>
                                                    </b-form-group>
                                                </validate>

                                                <validate auto-label>
                                                    <b-form-group label="Last Name">
                                                        <b-form-input name="lastName" v-model="accountModel.lastName" :disabled="!!userResource" required />
                                                        <field-messages name="lastName" show="$submitted">
                                                            <div slot="required" class="text-danger">
                                                                This field is required
                                                            </div>
                                                        </field-messages>
                                                    </b-form-group>
                                                </validate>

                                                <messages :messages="messages" />

                                                <b-button type="submit" variant="primary" size="lg" :disabled="loading">Next</b-button>
                                                <small class="float-right">
                                                    <b-button variant="link" size="sm" @click="hasAccount = true">Already Have an Account?</b-button>
                                                </small>
                                            </vue-form>
                                        </b-card>
                                    </b-col>
                                </b-row>

                                <b-row v-else-if="step == 2">
                                    <b-col md="6" offset-md="3">
                                        <b-card class="mt-5 mb-5">
                                            <vue-form :state="passwordFormState" @submit.prevent="onSubmitStep2">
                                                <validate auto-label :custom="{ minLength: (v) => String(v).length > 9 }">
                                                    <b-form-group label="Choose a Password">
                                                        <b-form-input type="password" name="password" v-model="accountModel.password" required />
                                                        <b-form-text>Must be at least 10 characters in length</b-form-text>
                                                        <field-messages name="password" show="$submitted">
                                                            <div slot="required" class="text-danger">
                                                                This field is required
                                                            </div>
                                                            <div slot="minLength" class="text-danger">
                                                                Must be at least 10 characters in length
                                                            </div>
                                                        </field-messages>
                                                    </b-form-group>
                                                </validate>

                                                <validate auto-label :custom="{ equalsPassword: (v) => v === accountModel.password }">
                                                    <b-form-group label="Confirm Password">
                                                        <b-form-input type="password" name="passwordConfirm" v-model="accountModel.passwordConfirm" required />
                                                        <field-messages name="passwordConfirm" show="$submitted">
                                                            <div slot="required" class="text-danger">
                                                                This field is required
                                                            </div>
                                                            <div slot="equalsPassword" class="text-danger">
                                                                Passwords must match
                                                            </div>
                                                        </field-messages>
                                                    </b-form-group>
                                                </validate>

                                                <messages :messages="messages" />

                                                <b-button type="submit" variant="primary" size="lg" :disabled="loading">Next</b-button>
                                            </vue-form>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab :active="step == 3" class="p-md-5" :disabled="step !== 3">
                                <template slot="title">
                                    <span v-if="step > 3" class="text-success"> <b-icon icon="check" />Company </span>
                                    <span v-else>Company</span>
                                </template>

                                <b-row>
                                    <b-col size="12">
                                        <h4>Company Details</h4>
                                        <p>Tell us a bit about your company. <span v-if="companyOptions.length > 0">Or you can also <a href="javascript:{}" v-b-modal.modal-choose-company>use an existing company</a></span></p>
                                        <hr />
                                    </b-col>
                                </b-row>

                                <b-row class="mt-3">
                                    <b-col md="3" class="text-center">
                                        <b-img rounded :src="companyImage" v-if="companyImageSelected" fluid thumbnail />
                                        <div class="text-center" v-else>
                                            <b-avatar size="7em" icon="building" variant="dark" rounded></b-avatar>
                                        </div>
                                        <!-- https://logo.clearbit.com/<company-url>?size=100 -->
                                        <image-upload class="mt-2" @success="onCropperSave" />
                                    </b-col>

                                    <b-col md="9">
                                        <b-card>
                                            <company-register-form @success="onSubmitStep3">
                                                <template slot="submit">
                                                    <b-button type="submit" variant="primary" size="lg" :disabled="loading">Next</b-button>
                                                </template>
                                            </company-register-form>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Job Details" :active="step == 4" class="p-5" :disabled="step !== 4">
                                <template slot="title">
                                    <span v-if="step > 4" class="text-success"> <b-icon icon="check" />Job Listing </span>
                                    <span v-else>Job Listing</span>
                                </template>

                                <b-row>
                                    <b-col size="12">
                                        <h4>Tell us about the position</h4>
                                        <p>You will have a chance to preview your job listing in the next step. Questions? Contact us.</p>
                                        <hr />
                                    </b-col>
                                </b-row>

                                <b-row class="mt-3">
                                    <b-col md="12">
                                        <b-card>
                                            <job-posting-form
                                                :user-resource="userResource"
                                                :company-resource="companyResource"
                                                v-if="userResource && companyResource && step == 4"
                                                @success="onSubmitStep4"
                                                submit-text="Preview"
                                                no-preview>
                                            </job-posting-form>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>
                            <b-tab title="Preview" :active="step == 5" class="p-5" :disabled="step !== 5">
                                <div v-if="listingResource" v-html="listingResource.get('body')" />
                            </b-tab>
                        </b-tabs>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <section v-if="step < 3">
            <b-container>
                <div class="section-heading text-center mb-0 mt-5">
                    <p>Example Profile</p>
                </div>

                <b-row class="mb-5">
                    <b-col md="8" offset-md="2">
                        <b-card class="mb-3">
                            <profile-cell :profile="demoProfile" v-if="demoProfile" />
                            <profile-cell v-else class="mb-3" />
                        </b-card>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- MODALS -->
        <b-modal id="modal-choose-company" title="Choose a Company" size="xs" @ok="onSubmitStep3(companyResource)" no-fade>
            <b-form-select v-model="companyResource" :options="companyOptions" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.button-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 17px;
}
</style>
